import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

// ─────────────────────────────────────────────────────────────
// GlobalErrorBoundary
//
// Catches any uncaught React render errors and shows a friendly
// fallback instead of a blank white screen.
//
// NOTE: This component is mounted OUTSIDE BrowserRouter in main.tsx
// so it can catch errors in the router itself. For this reason it
// must NOT use react-router's <Link> — use a plain <a> instead.
// ─────────────────────────────────────────────────────────────
export class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg-base)',
        padding: '2rem',
      }}>
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          padding: '2.5rem',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{
            color: 'var(--text-primary)',
            fontSize: '1.25rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
          }}>
            Something went wrong
          </h2>
          <p style={{
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            marginBottom: '1.5rem',
            lineHeight: 1.6,
          }}>
            An unexpected error occurred. You can try refreshing the page or going back home.
          </p>

          {/* Error details — dev only */}
          {import.meta.env.DEV && this.state.error && (
            <details style={{
              marginBottom: '1.5rem',
              textAlign: 'left',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '0.75rem',
            }}>
              <summary style={{
                cursor: 'pointer',
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                marginBottom: '0.5rem',
              }}>
                Error details (dev only)
              </summary>
              <pre style={{
                fontSize: '0.7rem',
                color: 'var(--error)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
              }}>
                {this.state.error.message}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.5rem 1.25rem',
                background: 'var(--accent-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              Refresh page
            </button>
            {/* Plain <a> — cannot use react-router Link here since we are outside BrowserRouter */}
            <a
              href="/"
              style={{
                padding: '0.5rem 1.25rem',
                background: 'transparent',
                color: 'var(--text-muted)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                display: 'inline-flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    )
  }
}
